(function() {
    'use strict';
    
    /**
     * MAIN TRACKER CONFIGURATION
     */
    const CONFIG = {
        cookieName: 'traffic_source_data',
        fieldName: 'traffic_source',        // Form field name attribute
        fieldId: 'traffic_source',          // Form field id attribute
        cookieExpiry: 30, // days
        observerDelay: 40, // ms
        pollInterval: 100, // ms
        maxPolls: 300 // stop polling after 30 seconds
    };
    
    /**
     * TRAFFIC SOURCE DETECTION ENGINE
     * Analyzes current visit and determines traffic source with smart attribution
     */
    const SourceDetector = {
        /**
         * Extract URL parameter (modern + legacy browser support)
         */
        getParam: function(name) {
            try {
                return new URLSearchParams(window.location.search).get(name);
            } catch (e) {
                // Fallback for older browsers
                const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
                const results = regex.exec(window.location.href);
                return results && results[2] ? decodeURIComponent(results[2].replace(/\+/g, ' ')) : null;
            }
        },
        
        /**
         * Get referrer domain safely
         */
        getReferrerDomain: function() {
            if (!document.referrer) return null;
            try {
                const a = document.createElement('a');
                a.href = document.referrer;
                return a.hostname;
            } catch (e) {
                return null;
            }
        },
        
        /**
         * Analyze current visit and determine traffic source
         * Returns structured attribution data
         */
        detect: function() {
            // Extract all tracking parameters
            const utm_source = this.getParam('utm_source');
            const utm_medium = this.getParam('utm_medium');
            const utm_campaign = this.getParam('utm_campaign');
            const utm_term = this.getParam('utm_term');
            const utm_content = this.getParam('utm_content');
            const gclid = this.getParam('gclid'); // Google Ads
            const fbclid = this.getParam('fbclid'); // Facebook Ads
            const referrer = this.getReferrerDomain();
            
            // Default attribution data
            const data = {
                source: 'direct',
                medium: 'none',
                campaign: '',
                term: '',
                content: '',
                timestamp: new Date().toISOString(),
                landing_page: window.location.href,
                user_agent: navigator.userAgent.substring(0, 200)
            };
            
            // Priority 1: UTM parameters (campaign tracking)
            if (utm_source) {
                data.source = utm_source;
                data.medium = utm_medium || 'unknown';
                data.campaign = utm_campaign || '';
                data.term = utm_term || '';
                data.content = utm_content || '';
            }
            // Priority 2: Google Ads (gclid parameter)
            else if (gclid) {
                data.source = 'google';
                data.medium = 'cpc';
                data.campaign = 'google_ads';
            }
            // Priority 3: Facebook Ads (fbclid parameter)
            else if (fbclid) {
                data.source = 'facebook';
                data.medium = 'cpc';
                data.campaign = 'facebook_ads';
            }
            // Priority 4: External referrers (ONLY if coming from external domain)
            else if (referrer && referrer !== window.location.hostname) {
                data.source = referrer;
                // Categorize by domain patterns
                if (referrer.indexOf('google') !== -1) data.medium = 'organic';
                else if (['facebook', 'linkedin', 'twitter', 't.co', 'instagram', 'youtube'].some(function(s) { return referrer.indexOf(s) !== -1; })) data.medium = 'social';
                else if (['bing', 'yahoo', 'duckduckgo'].some(function(s) { return referrer.indexOf(s) !== -1; })) data.medium = 'organic';
                else data.medium = 'referral';
            }
            // Priority 5: Internal navigation or Direct traffic
            // IMPORTANT: If referrer is same domain, treat as internal navigation (not a new source)
            else if (referrer && referrer === window.location.hostname) {
                // This is internal navigation - return null to indicate no new source detected
                return null;
            }
            // Priority 6: Direct traffic (no referrer at all)
            // Leave defaults: source: 'direct', medium: 'none'
            
            return data;
        },
        
        /**
         * Calculate source priority for "Never Downgrade" policy
         */
        getSourcePriority: function(source, medium) {
            if (medium === 'cpc') return 100; // Paid traffic
            if (source.indexOf('google') !== -1 || medium === 'organic') return 80; // Search engines
            if (medium === 'social' || medium === 'referral') return 80; // External sources
            if (source === 'direct') return 10; // Direct traffic
            return 50; // Unknown external
        }
    };
    
    /**
     * COOKIE STORAGE MANAGER
     * Handles secure first-party cookie storage with JSON serialization
     */
    const Storage = {
        /**
         * Save data to cookie with expiration
         */
        set: function(name, data, days) {
            const expires = days ? new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString() : '';
            const expiresStr = expires ? '; expires=' + expires : '';
            document.cookie = name + '=' + encodeURIComponent(JSON.stringify(data)) + expiresStr + '; path=/; SameSite=Lax';
        },
        
        /**
         * Retrieve and parse data from cookie
         */
        get: function(name) {
            const nameEQ = name + '=';
            const cookies = document.cookie.split(';');
            
            for (let i = 0; i < cookies.length; i++) {
                let cookie = cookies[i].trim();
                if (cookie.indexOf(nameEQ) === 0) {
                    try {
                        return JSON.parse(decodeURIComponent(cookie.substring(nameEQ.length)));
                    } catch (e) {
                        console.warn('Traffic Tracker: Invalid cookie data');
                        return null;
                    }
                }
            }
            return null;
        },
        
        /**
         * Clear cookie data
         */
        clear: function(name) {
            document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
    };
    
    /**
     * SMART FORM FIELD DETECTOR
     * Finds traffic_source fields across all documents including iframes and shadow DOM
     */
    const FieldDetector = {
        /**
         * Check if element matches our target field
         */
        isTargetField: function(el) {
            if (!el || el.nodeType !== 1) return false;
            
            try {
                // Check various identifying attributes
                return (
                    el.name === CONFIG.fieldName ||
                    el.id === CONFIG.fieldId ||
                    (el.getAttribute && el.getAttribute('data-testid') === CONFIG.fieldId) ||
                    (el.getAttribute && el.getAttribute('aria-labelledby') === CONFIG.fieldId) ||
                    (el.id && el.id.indexOf(CONFIG.fieldId) !== -1)
                );
            } catch (e) {
                return false;
            }
        },
        
        /**
         * Find all target fields in a document root (including shadow DOM)
         */
        findInRoot: function(root) {
            if (!root) return [];
            
            const found = [];
            
            // Try specific selectors first
            const selectors = [
                'input[name="' + CONFIG.fieldName + '"]',
                'input[id="' + CONFIG.fieldId + '"]',
                'input[id="' + CONFIG.fieldId.replace('#', '\\#') + '"]',
                'input[data-testid="' + CONFIG.fieldId + '"]',
                'input[aria-labelledby="' + CONFIG.fieldId + '"]',
                'input[id*="' + CONFIG.fieldId + '"]'
            ];
            
            // Query with each selector
            for (let i = 0; i < selectors.length; i++) {
                try {
                    if (root.querySelectorAll) {
                        const elements = root.querySelectorAll(selectors[i]);
                        for (let j = 0; j < elements.length; j++) {
                            if (found.indexOf(elements[j]) === -1) {
                                found.push(elements[j]);
                            }
                        }
                    }
                } catch (e) {
                    // Selector may not be supported in some contexts
                }
            }
            
            // Fallback: scan all inputs
            if (found.length === 0) {
                try {
                    if (root.querySelectorAll) {
                        const inputs = root.querySelectorAll('input');
                        for (let i = 0; i < inputs.length; i++) {
                            if (this.isTargetField(inputs[i]) && found.indexOf(inputs[i]) === -1) {
                                found.push(inputs[i]);
                            }
                        }
                    }
                } catch (e) {}
            }
            
            // Check shadow DOM
            try {
                if (root.querySelectorAll) {
                    const allElements = root.querySelectorAll('*');
                    for (let i = 0; i < allElements.length; i++) {
                        if (allElements[i].shadowRoot) {
                            const shadowFound = this.findInRoot(allElements[i].shadowRoot);
                            for (let j = 0; j < shadowFound.length; j++) {
                                if (found.indexOf(shadowFound[j]) === -1) {
                                    found.push(shadowFound[j]);
                                }
                            }
                        }
                    }
                }
            } catch (e) {}
            
            return found;
        },
        
        /**
         * Find fields across all documents (including iframes)
         */
        findAll: function(doc) {
            doc = doc || document;
            let results = this.findInRoot(doc);
            
            // Search in iframes
            try {
                const iframes = doc.getElementsByTagName('iframe');
                for (let i = 0; i < iframes.length; i++) {
                    try {
                        const childDoc = iframes[i].contentDocument || (iframes[i].contentWindow && iframes[i].contentWindow.document);
                        if (childDoc && childDoc !== doc) {
                            const iframeResults = this.findAll(childDoc);
                            results = results.concat(iframeResults);
                        }
                    } catch (e) {
                        // Cross-origin iframe - skip
                    }
                }
            } catch (e) {}
            
            return results;
        }
    };
    
    /**
     * FORM FIELD UPDATER
     * Updates form fields with tracking data and ensures proper field configuration
     */
    const FieldUpdater = {
        /**
         * Update a single field with tracking data
         */
        updateField: function(field, data) {
            if (!field) return false;
            
            let changed = false;
            const dataString = JSON.stringify(data);
            
            try {
                // Update value if different
                if (field.value !== dataString) {
                    field.value = dataString;
                    field.setAttribute('value', dataString);
                    
                    // Trigger events for frameworks
                    field.dispatchEvent(new Event('input', { bubbles: true }));
                    field.dispatchEvent(new Event('change', { bubbles: true }));
                    changed = true;
                }
                
                // Ensure field is properly hidden
                this.ensureHidden(field);
                
            } catch (e) {
                console.warn('Traffic Tracker: Error updating field', e);
            }
            
            return changed;
        },
        
        /**
         * Ensure field is properly hidden with multiple techniques
         */
        ensureHidden: function(field) {
            try {
                field.type = 'hidden';
                field.style.display = 'none';
                field.style.visibility = 'hidden';
                field.style.position = 'absolute';
                field.style.left = '-9999px';
                field.setAttribute('aria-hidden', 'true');
                field.setAttribute('tabindex', '-1');
            } catch (e) {}
        },
        
        /**
         * Update all detected fields with current tracking data
         */
        updateAll: function(data) {
            const fields = FieldDetector.findAll();
            let updatedCount = 0;
            
            for (let i = 0; i < fields.length; i++) {
                if (this.updateField(fields[i], data)) {
                    updatedCount++;
                }
            }
            
            if (updatedCount > 0) {
                console.log('Traffic Tracker: Updated ' + updatedCount + ' form field(s)');
            }
            
            return updatedCount;
        }
    };
    
    /**
     * MAIN TRACKING CONTROLLER
     * Orchestrates attribution logic and form updates
     */
    const TrafficTracker = {
        /**
         * Smart attribution with "Never Downgrade" policy
         * Only updates attribution if new source is higher priority
         */
        updateAttribution: function() {
            console.log('🎯 Traffic Tracker: Analyzing attribution...');
            
            const existing = Storage.get(CONFIG.cookieName);
            const current = SourceDetector.detect();
            
            // If current detection returns null (internal navigation), preserve existing data
            if (current === null) {
                console.log('🏠 Internal navigation detected - preserving existing attribution');
                if (existing) {
                    FieldUpdater.updateAll(existing);
                    console.log('🏁 Final attribution (preserved):', existing);
                    return existing;
                } else {
                    // First visit but internal navigation - shouldn't happen, but handle gracefully
                    const directData = {
                        source: 'direct',
                        medium: 'none', 
                        campaign: '',
                        term: '',
                        content: '',
                        timestamp: new Date().toISOString(),
                        landing_page: window.location.href,
                        user_agent: navigator.userAgent.substring(0, 200)
                    };
                    Storage.set(CONFIG.cookieName, directData, CONFIG.cookieExpiry);
                    FieldUpdater.updateAll(directData);
                    console.log('🏁 Final attribution (new direct):', directData);
                    return directData;
                }
            }
            
            console.log('📊 Current visit:', current);
            if (existing) console.log('💾 Existing attribution:', existing);
            
            let shouldUpdate = false;
            let reason = '';
            
            if (!existing) {
                shouldUpdate = true;
                reason = 'First visit';
            }
            // UTM parameters always win (campaign tracking priority)
            else if (SourceDetector.getParam('utm_source')) {
                shouldUpdate = true;
                reason = 'UTM parameters present (highest priority)';
            }
            // Never downgrade: only update if new source has higher priority OR equal external sources
            else {
                const currentPriority = SourceDetector.getSourcePriority(current.source, current.medium);
                const existingPriority = SourceDetector.getSourcePriority(existing.source, existing.medium);
                
                // Check if both are external sources (not direct/internal)
                const currentIsExternal = current.source !== 'direct' && current.source !== window.location.hostname;
                const existingIsExternal = existing.source !== 'direct' && existing.source !== window.location.hostname;
                
                if (currentPriority > existingPriority) {
                    shouldUpdate = true;
                    reason = 'Upgrading priority: ' + existingPriority + ' → ' + currentPriority;
                }
                // Update if current is external and has equal priority (most recent external source wins)
                else if (currentPriority === existingPriority && currentIsExternal && existingIsExternal) {
                    shouldUpdate = true;
                    reason = 'Equal priority external sources - updating to most recent (' + current.source + ')';
                }
                // NEVER DOWNGRADE: Don't replace external with direct/internal
                else if (existingIsExternal && !currentIsExternal) {
                    shouldUpdate = false;
                    reason = '🛡️ NEVER DOWNGRADE: Protecting external source from direct/internal navigation';
                }
                else {
                    reason = '🛡️ Never Downgrade: Preserving higher priority source (' + existingPriority + ' ≥ ' + currentPriority + ')';
                }
            }
            
            // Apply attribution decision
            const finalData = shouldUpdate ? current : existing;
            
            if (shouldUpdate) {
                Storage.set(CONFIG.cookieName, current, CONFIG.cookieExpiry);
                console.log('✅ Attribution updated - ' + reason);
            } else {
                console.log('⏭️ Attribution preserved - ' + reason);
            }
            
            // Update all form fields
            FieldUpdater.updateAll(finalData);
            
            // Send to Google Analytics if available
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'traffic_source_tracked',
                    traffic_source: finalData.source,
                    traffic_medium: finalData.medium,
                    traffic_campaign: finalData.campaign
                });
                console.log('📈 Data sent to Google Analytics');
            }
            
            console.log('🏁 Final attribution:', finalData);
            return finalData;
        },
        
        /**
         * Set up dynamic content monitoring
         */
        startMonitoring: function() {
            let pollCount = 0;
            let scheduled = false;
            const self = this;
            
            // Mutation observer for DOM changes
            const observer = new MutationObserver(function() {
                if (scheduled) return;
                scheduled = true;
                setTimeout(function() {
                    const data = Storage.get(CONFIG.cookieName);
                    if (data) FieldUpdater.updateAll(data);
                    scheduled = false;
                }, CONFIG.observerDelay);
            });
            
            // Start observing all documents
            const observeDoc = function(doc) {
                try {
                    observer.observe(doc.documentElement || doc, {
                        childList: true,
                        subtree: true,
                        attributes: true
                    });
                } catch (e) {}
                
                // Observe iframes
                try {
                    const iframes = doc.getElementsByTagName('iframe');
                    for (let i = 0; i < iframes.length; i++) {
                        try {
                            const childDoc = iframes[i].contentDocument || (iframes[i].contentWindow && iframes[i].contentWindow.document);
                            if (childDoc && childDoc !== doc) observeDoc(childDoc);
                        } catch (e) {}
                    }
                } catch (e) {}
            };
            
            observeDoc(document);
            
            // Polling fallback for complex dynamic content
            const poll = setInterval(function() {
                const data = Storage.get(CONFIG.cookieName);
                if (data) FieldUpdater.updateAll(data);
                
                pollCount++;
                if (pollCount >= CONFIG.maxPolls) {
                    clearInterval(poll);
                    console.log('Traffic Tracker: Polling completed');
                }
            }, CONFIG.pollInterval);
            
            // Cleanup functions
            window.__trafficTracker_stop = function() {
                observer.disconnect();
                clearInterval(poll);
                console.log('Traffic Tracker: Monitoring stopped');
            };
        },
        
        /**
         * Initialize the complete tracking system
         */
        init: function() {
            console.log('🚀 Traffic Tracker v3.0: Starting unified tracking system...');
            console.log('🌐 URL:', window.location.href);
            console.log('📄 Referrer:', document.referrer || 'Direct');
            
            const self = this;
            
            // Start tracking when DOM is ready
            const startTracking = function() {
                console.log('✅ DOM ready - starting attribution');
                self.updateAttribution();
                self.startMonitoring();
                console.log('🛡️ Never Downgrade Policy: Active');
                console.log('👀 Form monitoring: Active');
            };
            
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', startTracking);
            } else {
                startTracking();
            }
            
            // Expose debug interface
            this.exposeDebugInterface();
        },
        
        /**
         * Expose debugging tools to window
         */
        exposeDebugInterface: function() {
            window.TrafficTracker = {
                /**
                 * Get current tracking data
                 */
                getData: function() {
                    const data = Storage.get(CONFIG.cookieName);
                    console.log('📋 Current tracking data:', data);
                    return data;
                },
                
                /**
                 * Force refresh attribution
                 */
                refresh: function() {
                    console.log('🔄 Manual refresh...');
                    return TrafficTracker.updateAttribution();
                },
                
                /**
                 * Clear all tracking data
                 */
                clear: function() {
                    Storage.clear(CONFIG.cookieName);
                    console.log('🗑️ Tracking data cleared');
                    console.log('💡 Refresh page to restart tracking');
                },
                
                /**
                 * Test form field detection
                 */
                testFields: function() {
                    const fields = FieldDetector.findAll();
                    console.log('🔍 Found ' + fields.length + ' traffic_source field(s):', fields);
                    for (let i = 0; i < fields.length; i++) {
                        console.log('Field ' + (i + 1) + ':', {
                            element: fields[i],
                            name: fields[i].name,
                            id: fields[i].id,
                            value: fields[i].value
                        });
                    }
                    return fields;
                },
                
                /**
                 * Test current attribution priority
                 */
                testPriority: function() {
                    const current = SourceDetector.detect();
                    const existing = Storage.get(CONFIG.cookieName);
                    const currentPriority = SourceDetector.getSourcePriority(current.source, current.medium);
                    const existingPriority = existing ? SourceDetector.getSourcePriority(existing.source, existing.medium) : 0;
                    
                    // Check if both are external sources
                    const currentIsExternal = current.source !== 'direct' && current.source !== window.location.hostname;
                    const existingIsExternal = existing && existing.source !== 'direct' && existing.source !== window.location.hostname;
                    
                    // Determine if should update based on same logic as main function
                    let wouldUpdate = false;
                    if (!existing) {
                        wouldUpdate = true;
                    } else if (SourceDetector.getParam('utm_source')) {
                        wouldUpdate = true;
                    } else if (currentPriority > existingPriority) {
                        wouldUpdate = true;
                    } else if (currentPriority === existingPriority && currentIsExternal && existingIsExternal) {
                        wouldUpdate = true; // EQUAL PRIORITY EXTERNAL SOURCES - LAST ONE WINS
                    }
                    
                    console.log('🧪 Attribution Priority Test:');
                    console.log('Current visit:', current, '(Priority: ' + currentPriority + ', External: ' + currentIsExternal + ')');
                    console.log('Existing data:', existing, '(Priority: ' + existingPriority + ', External: ' + existingIsExternal + ')');
                    console.log('Would update:', wouldUpdate);
                    console.log('Reason:', !existing ? 'First visit' : 
                               SourceDetector.getParam('utm_source') ? 'UTM parameters' :
                               currentPriority > existingPriority ? 'Higher priority' :
                               currentPriority === existingPriority && currentIsExternal && existingIsExternal ? 'Equal external - most recent wins' :
                               'No update needed');
                }
            };
            
            console.log('💡 Debug Interface Ready:');
            console.log('   TrafficTracker.getData()     - View current data');
            console.log('   TrafficTracker.refresh()     - Force refresh');
            console.log('   TrafficTracker.clear()       - Clear data');
            console.log('   TrafficTracker.testFields()  - Test field detection');
            console.log('   TrafficTracker.testPriority() - Test priority logic');
        }
    };
    
    // Initialize the complete tracking system
    TrafficTracker.init();
    
})();